import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

/** Pixels from the bottom of the scroller treated as “pinned to latest” (exported for tests). */
export const ADVISOR_SCROLL_NEAR_BOTTOM_PX = 56;

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
}

function resolveScrollBehavior(behavior: ScrollBehavior): ScrollBehavior {
  if (behavior === 'smooth' && prefersReducedMotion()) return 'instant';
  return behavior;
}

function scrollMessagesEndIntoView(
  scrollRoot: HTMLElement | null,
  messagesEnd: HTMLElement | null,
  behavior: ScrollBehavior
) {
  const resolved = resolveScrollBehavior(behavior);
  // Scroll the known transcript root directly. `scrollIntoView` on the end marker
  // can target outer page scrollers first and leave the panel short of the bottom.
  if (scrollRoot) {
    const top = Math.max(0, scrollRoot.scrollHeight - scrollRoot.clientHeight);
    if (typeof scrollRoot.scrollTo === 'function') {
      scrollRoot.scrollTo({ top, behavior: resolved });
    } else {
      scrollRoot.scrollTop = top;
    }
    return;
  }
  if (messagesEnd && typeof messagesEnd.scrollIntoView === 'function') {
    messagesEnd.scrollIntoView({ behavior: resolved, block: 'end' });
  }
}

export type UseAdvisorMessagesScrollOptions = {
  /** Chat messages from the UI SDK; dependency bumps on every streamed token. */
  messages: readonly unknown[];
  /** True while the assistant request is in flight (submitted or streaming). */
  busy: boolean;
    enabled?: boolean;
};

export type UseAdvisorMessagesScrollResult = {
  scrollRootRef: RefObject<HTMLDivElement | null>;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  /** True when `busy` and the user has scrolled away from the bottom. */
  showJumpToLatest: boolean;
  scrollToLatest: () => void;
};

/**
 * Keeps the transcript pinned to the bottom while the user stays at the bottom;
 * when they scroll up during a stream, auto-scroll stops and {@link showJumpToLatest} becomes true.
 */
export function useAdvisorMessagesScroll({
  messages,
  busy,
  enabled = true,
}: UseAdvisorMessagesScrollOptions): UseAdvisorMessagesScrollResult {
  const scrollRootRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevLenRef = useRef(messages.length);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  const isNearBottom = useCallback((el: HTMLElement) => {
    if (el.scrollHeight <= el.clientHeight + ADVISOR_SCROLL_NEAR_BOTTOM_PX) return true;
    const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
    return gap < ADVISOR_SCROLL_NEAR_BOTTOM_PX;
  }, []);

  const updateJumpVisibility = useCallback(() => {
    const el = scrollRootRef.current;
    if (!el) {
      setShowJumpToLatest(false);
      return;
    }
    if (!enabled) {
      setShowJumpToLatest(false);
      return;
    }
    setShowJumpToLatest(busy && !isNearBottom(el));
  }, [busy, enabled, isNearBottom]);

  useEffect(() => {
    if (!enabled) {
      setShowJumpToLatest(false);
      return;
    }
    const el = scrollRootRef.current;
    if (!el) return;
    const onScroll = () => updateJumpVisibility();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [enabled, updateJumpVisibility]);

  useEffect(() => {
    updateJumpVisibility();
  }, [busy, updateJumpVisibility]);

  useEffect(() => {
    if (!enabled) {
      prevLenRef.current = messages.length;
      setShowJumpToLatest(false);
      return;
    }

    const prevLen = prevLenRef.current;
    const nextLen = messages.length;
    const lenIncreased = nextLen > prevLen;
    const lenDecreased = nextLen < prevLen;
    prevLenRef.current = nextLen;

    const id = requestAnimationFrame(() => {
      const root = scrollRootRef.current;
      const scrollToEnd = (behavior: ScrollBehavior) => {
        scrollMessagesEndIntoView(root, messagesEndRef.current, behavior);
        setShowJumpToLatest(false);
      };

      if (!root) {
        scrollMessagesEndIntoView(null, messagesEndRef.current, lenIncreased ? 'smooth' : 'instant');
        setShowJumpToLatest(false);
        return;
      }

      // Fewer messages (clear chat / thread switch): always pin to end; same-length updates fall through.
      if (lenDecreased || lenIncreased) {
        scrollToEnd(lenIncreased ? 'smooth' : 'instant');
        return;
      }

      if (isNearBottom(root)) {
        scrollToEnd('instant');
      } else {
        setShowJumpToLatest(busy && !isNearBottom(root));
      }
    });

    return () => cancelAnimationFrame(id);
  }, [messages, busy, enabled, isNearBottom]);

  const scrollToLatest = useCallback(() => {
    scrollMessagesEndIntoView(scrollRootRef.current, messagesEndRef.current, 'smooth');
    setShowJumpToLatest(false);
  }, []);

  /** When the surface becomes active again, pin to the latest line (drawer reopen). */
  useEffect(() => {
    if (!enabled) return;
    const id = requestAnimationFrame(() => {
      const root = scrollRootRef.current;
      if (!root) return;
      scrollMessagesEndIntoView(root, messagesEndRef.current, 'instant');
      setShowJumpToLatest(false);
    });
    return () => cancelAnimationFrame(id);
  }, [enabled]);

  return { scrollRootRef, messagesEndRef, showJumpToLatest, scrollToLatest };
}

/** @alias {@link useAdvisorMessagesScroll} */
export const useAgentMessagesScroll = useAdvisorMessagesScroll;
